import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { authFetch, API } from "../utils/api";
import { STATUS_LABELS } from "./AdminDashboard.constants";

// ── Data fetching ─────────────────────────────────────────────────────────────

export function usePipelineData() {
  const [businesses, setBusinesses] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [pendingWidgetsCount, setPendingWidgetsCount] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [bRes, aRes, wRes, uRes] = await Promise.all([
        authFetch(`${API}/businesses`),
        authFetch(`${API}/analytics`),
        authFetch(`${API}/admin/widgets/pending`),
        authFetch(`${API}/users`),
      ]);
      if (bRes?.ok) {
        const bizData = await bRes.json();
        setBusinesses(Array.isArray(bizData) ? bizData : []);
      }
      if (aRes?.ok) setAnalytics(await aRes.json());
      if (wRes?.ok) {
        const wd = await wRes.json();
        setPendingWidgetsCount((wd.widgets || []).length);
      }
      if (uRes?.ok) setUsers(await uRes.json());
    } catch {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  return { businesses, analytics, loading, users, pendingWidgetsCount, setPendingWidgetsCount, fetchData };
}

// ── Bulk selection & actions ──────────────────────────────────────────────────

export function useBulkActions(filtered, fetchData) {
  const [selected, setSelected] = useState(new Set());
  const [showStatusDrop, setShowStatusDrop] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  const toggleSelect = (id) =>
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const selectAll = () =>
    setSelected(prev =>
      prev.size === filtered.length ? new Set() : new Set(filtered.map(b => b.id))
    );

  const clearSelected = () => setSelected(new Set());

  const bulkDelete = async () => {
    if (!window.confirm(`¿Eliminar ${selected.size} empresa(s)?`)) return;
    setBulkLoading(true);
    try {
      const res = await authFetch(`${API}/businesses/bulk`, {
        method: "DELETE",
        body: JSON.stringify({ ids: Array.from(selected) }),
      });
      if (!res?.ok) throw new Error();
      toast.success(`${selected.size} empresa(s) eliminadas`);
      clearSelected();
      fetchData();
    } catch {
      toast.error("Error al eliminar");
    } finally {
      setBulkLoading(false);
    }
  };

  const bulkSetStatus = async (status) => {
    setBulkLoading(true);
    setShowStatusDrop(false);
    try {
      const res = await authFetch(`${API}/businesses/bulk-status`, {
        method: "PATCH",
        body: JSON.stringify({ ids: Array.from(selected), status }),
      });
      if (!res?.ok) throw new Error();
      toast.success(`${selected.size} empresa(s) actualizadas a "${STATUS_LABELS[status]}"`);
      clearSelected();
      fetchData();
    } catch {
      toast.error("Error al actualizar");
    } finally {
      setBulkLoading(false);
    }
  };

  return {
    selected,
    showStatusDrop, setShowStatusDrop,
    bulkLoading,
    toggleSelect,
    selectAll,
    clearSelected,
    bulkDelete,
    bulkSetStatus,
    allSelected: filtered.length > 0 && selected.size === filtered.length,
    someSelected: selected.size > 0,
  };
}
